"use client";

import { createContext, useContext, useLayoutEffect, useState } from "react";
import { usePathname } from "next/navigation";

type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Landing/legal pages — use the "theme" localStorage key
const LANDING_PATHS = ["/", "/contact", "/terms", "/privacy", "/cookies"];

function getStorageKey(isLanding: boolean) {
  return isLanding ? "theme" : "app-theme";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");
  const pathname = usePathname();
  const isLanding = LANDING_PATHS.includes(pathname);

  useLayoutEffect(() => {
    const key = getStorageKey(isLanding);
    const saved = localStorage.getItem(key) as Theme | null;
    const resolved: Theme = saved === "light" ? "light" : "dark";
    setThemeState(resolved);
    document.documentElement.classList.toggle("light", resolved === "light");
  }, [pathname, isLanding]);

  const setTheme = (newTheme: Theme) => {
    const key = getStorageKey(isLanding);
    setThemeState(newTheme);
    localStorage.setItem(key, newTheme);
    document.documentElement.classList.toggle("light", newTheme === "light");
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
